import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AnimeCard from '@/components/feature/AnimeCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categoryInfo, getAnimeByCategory } from '@/mocks/animeData';
import type { AnimeItem } from '@/mocks/animeData';

const categoryKeys = ['korean', 'japanese', 'chinese'] as const;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const validType = categoryKeys.includes(type as typeof categoryKeys[number]) ? type as typeof categoryKeys[number] : 'korean';
  const cat = categoryInfo[validType];

  const [activeTag, setActiveTag] = useState<string>('全部');
  const [sortBy, setSortBy] = useState<string>('rating');

  const allAnime = getAnimeByCategory(validType);

  const allTags = ['全部', ...Array.from(new Set(allAnime.flatMap(a => a.tags)))];

  let filtered = activeTag === '全部' ? allAnime : allAnime.filter(a => a.tags.includes(activeTag));

  if (sortBy === 'rating') {
    filtered = [...filtered].sort((a, b) => b.rating - a.rating);
  } else if (sortBy === 'latest') {
    filtered = [...filtered].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  } else if (sortBy === 'views') {
    filtered = [...filtered].sort((a, b) => {
      const va = parseInt(a.views.replace(/[^0-9]/g, ''));
      const vb = parseInt(b.views.replace(/[^0-9]/g, ''));
      return vb - va;
    });
  }

  useEffect(() => {
    setActiveTag('全部');
    setSortBy('rating');
  }, [type]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${cat.name} - 51动漫`,
    description: cat.desc,
    url: `https://51anime.com/category/${cat.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '51动漫',
      url: 'https://51anime.com',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51anime.com' },
        { '@type': 'ListItem', position: 2, name: cat.name, item: `https://51anime.com/category/${cat.slug}` },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Category Header */}
        <section className="relative pt-24 pb-12 md:pt-28 md:pb-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: cat.name },
            ]} />
            <div className="flex items-center gap-4 mt-2">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100">
                <i className={`${cat.icon} text-accent-600 text-2xl`} />
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{cat.name}</h1>
                <p className="text-foreground-500 text-sm mt-1">{cat.desc}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Filters & Content */}
        <section className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              {categoryKeys.map((key) => {
                const c = categoryInfo[key];
                const isActiveTab = key === validType;
                return (
                  <Link
                    key={key}
                    to={`/category/${c.slug}`}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      isActiveTab
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {c.name}
                  </Link>
                );
              })}
            </div>

            {/* Tag Filter & Sort */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setActiveTag(tag)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeTag === tag
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-foreground-400 text-xs whitespace-nowrap">排序：</span>
                {[
                  { value: 'rating', label: '评分最高' },
                  { value: 'latest', label: '最近更新' },
                  { value: 'views', label: '最受欢迎' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-foreground-900 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Anime Grid */}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                {filtered.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-foreground-300 text-2xl" />
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无匹配的动漫作品</p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}